import * as THREE from 'three';

/**
 * Deterministic 32-bit PRNG (Mulberry32) so procedural cities and specimens
 * are 100% reproducible and inspectable by seed.
 */
export function createPRNG(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function randRange(rng: () => number, min: number, max: number): number {
  return min + (max - min) * rng();
}

export function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

export function damp(current: number, target: number, lambda: number, dt: number): number {
  return THREE.MathUtils.lerp(current, target, 1 - Math.exp(-lambda * dt));
}

export function dampVec3(
  current: THREE.Vector3,
  target: THREE.Vector3,
  lambda: number,
  dt: number
): void {
  const t = 1 - Math.exp(-lambda * dt);
  current.lerp(target, t);
}

/**
 * Logarithmic spiral point in 2D/3D: r(theta) = a * exp(b * theta)
 */
export function logarithmicSpiralPoint(
  theta: number,
  a: number,
  b: number,
  heightSlope = 0
): THREE.Vector3 {
  const r = a * Math.exp(b * theta);
  return new THREE.Vector3(r * Math.cos(theta), theta * heightSlope, r * Math.sin(theta));
}

/**
 * Computes vertex & triangle counts across any THREE.Object3D hierarchy for the Inspector.
 */
export function countGeometryStats(root: THREE.Object3D): {
  vertices: number;
  triangles: number;
  meshes: number;
} {
  let vertices = 0;
  let triangles = 0;
  let meshes = 0;

  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      const geom = obj.geometry;
      if (!geom) return;
      meshes += 1;
      const count = obj instanceof THREE.InstancedMesh ? obj.count : 1;
      const posAttr = geom.getAttribute('position');
      if (posAttr) {
        vertices += posAttr.count * count;
      }
      if (geom.index) {
        triangles += (geom.index.count / 3) * count;
      } else if (posAttr) {
        triangles += (posAttr.count / 3) * count;
      }
    }
  });

  return {
    vertices: Math.round(vertices),
    triangles: Math.round(triangles),
    meshes,
  };
}
